import _ from 'lodash';

import { collapseConnectionsByUser, normalizeUserId } from 'utils/presence';

import { MILLISECONDS_PER_MINUTE, MINUTES_PER_HOUR } from './PresenceAvatarGroup.constants';

export const buildNameKey = (firstName, lastName) => {
  const first = (firstName || '').trim().toLowerCase();
  const last = (lastName || '').trim().toLowerCase();

  return first || last ? `${first}|${last}` : null;
};

export const getIdleMinutes = lastActivityAt => {
  if (_.isNil(lastActivityAt)) {
    return 0;
  }

  const now = Date.now();
  const lastActivity = new Date(lastActivityAt).getTime();

  return Math.floor((now - lastActivity) / MILLISECONDS_PER_MINUTE);
};

// `occurredAt` is stamped client-side the moment this connection's USER_JOINED event is
// processed (see userJoinedAction) — accurate for anyone who joins after we did, unavailable for
// anyone the presence snapshot already listed when we connected (the server snapshot carries no
// join timestamp), in which case there is nothing honest to show.
export const getOpenDurationLabel = occurredAt => {
  if (_.isNil(occurredAt)) {
    return null;
  }

  const minutes = Math.floor((Date.now() - occurredAt) / MILLISECONDS_PER_MINUTE);

  if (minutes < 1) {
    return 'Open for less than a minute';
  }

  if (minutes < MINUTES_PER_HOUR) {
    return `Open for ${minutes} min`;
  }

  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const remainingMinutes = minutes % MINUTES_PER_HOUR;

  return remainingMinutes ? `Open for ${hours} hr ${remainingMinutes} min` : `Open for ${hours} hr`;
};

export const parseDisplayName = (displayName = '') => {
  const parts = (displayName || '').trim().split(' ');

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || '',
  };
};

const getPresenceEntryNameKey = entry => {
  const fallback = parseDisplayName(entry.displayName);

  return buildNameKey(entry.firstName || fallback.firstName, entry.lastName || fallback.lastName);
};

// The summary-contacts endpoint returns ONE entry per loan-role assignment, so a user in several
// roles arrives as several entries. Collapse them into one row per person carrying every roleName —
// but ONLY on a STRONG identity (userId, else emailAddress). A shared display name is deliberately
// NOT enough: two different people can share a name, so name-only entries stay separate (same stance
// as the assigned↔presence matcher below, which never cross-matches on name alone).
const getAssignedIdentityKey = user => {
  const normalizedUserId = normalizeUserId(user.userId);

  if (!_.isNil(normalizedUserId)) {
    return `id:${normalizedUserId}`;
  }

  const email = (user.emailAddress || '').trim().toLowerCase();

  return email ? `email:${email}` : null;
};

const getUserRoleNames = user => {
  if (Array.isArray(user.roleNames)) {
    return user.roleNames.filter(role => !_.isNil(role));
  }

  return _.isNil(user.roleName) ? [] : [user.roleName];
};

export const collapseAssignedUsersByPerson = (assignedUsers = []) => {
  const collapsed = [];
  const indexByKey = new Map();

  assignedUsers.forEach(user => {
    const roleNames = getUserRoleNames(user);
    const key = getAssignedIdentityKey(user);
    const existingIndex = _.isNil(key) ? null : indexByKey.get(key);

    if (_.isNil(existingIndex)) {
      if (!_.isNil(key)) {
        indexByKey.set(key, collapsed.length);
      }

      collapsed.push({ ...user, roleName: roleNames[0] ?? null, roleNames });

      return;
    }

    const existing = collapsed[existingIndex];
    const mergedRoleNames = _.union(existing.roleNames, roleNames);

    collapsed[existingIndex] = {
      ...existing,
      roleName: mergedRoleNames[0] ?? null,
      roleNames: mergedRoleNames,
    };
  });

  return collapsed;
};

export const mergeAssignedAndPresenceUsers = (
  assignedUsers = [],
  presenceUsers = [],
  currentUserId = null,
  { excludeCurrentUser = false } = {},
) => {
  const normalizedCurrentUserId = normalizeUserId(currentUserId);

  const presenceEntries = collapseConnectionsByUser(presenceUsers);

  const presenceByUserId = new Map();
  const presenceByNameKey = new Map();
  const matchedConnectionIds = new Set();

  presenceEntries.forEach(entry => {
    const normalizedEntryUserId = normalizeUserId(entry.userId);

    if (!_.isNil(normalizedEntryUserId)) {
      presenceByUserId.set(normalizedEntryUserId, entry);
    }

    const nameKey = getPresenceEntryNameKey(entry);

    if (!_.isNil(nameKey)) {
      presenceByNameKey.set(nameKey, entry);
    }
  });

  const assignedWithPresence = collapseAssignedUsersByPerson(assignedUsers).map(user => {
    const normalizedUserId = normalizeUserId(user.userId);
    const userIdMatch = !_.isNil(normalizedUserId) ? presenceByUserId.get(normalizedUserId) : null;
    const nameKey = buildNameKey(user.firstName, user.lastName);
    const nameKeyMatch =
      _.isNil(userIdMatch) && !_.isNil(nameKey) ? presenceByNameKey.get(nameKey) : null;
    const matchedEntry = userIdMatch ?? nameKeyMatch;

    if (matchedEntry) {
      matchedConnectionIds.add(matchedEntry.connectionId);
    }

    if (nameKeyMatch) {
      presenceByNameKey.delete(nameKey);
    }

    return {
      ...user,
      userId: user.userId ?? matchedEntry?.userId ?? null,
      presenceData: matchedEntry ?? null,
      isAssigned: true,
    };
  });

  const presentButNotAssigned = presenceEntries
    .filter(entry => !matchedConnectionIds.has(entry.connectionId))
    .filter(
      entry =>
        !excludeCurrentUser ||
        _.isNil(normalizedCurrentUserId) ||
        normalizeUserId(entry.userId) !== normalizedCurrentUserId,
    )
    .map(entry => {
      const fallback = parseDisplayName(entry.displayName);

      return {
        userId: entry.userId,
        firstName: entry.firstName || fallback.firstName,
        lastName: entry.lastName || fallback.lastName,
        roleName: null,
        roleNames: [],
        presenceData: entry,
        isAssigned: false,
      };
    });

  const merged = [...assignedWithPresence, ...presentButNotAssigned];

  if (_.isNil(normalizedCurrentUserId)) {
    return merged;
  }

  return merged.sort((a, b) => {
    const aIsCurrent = normalizeUserId(a.userId) === normalizedCurrentUserId;
    const bIsCurrent = normalizeUserId(b.userId) === normalizedCurrentUserId;

    if (aIsCurrent && !bIsCurrent) {
      return -1;
    }

    if (!aIsCurrent && bIsCurrent) {
      return 1;
    }

    return 0;
  });
};
